import { Map, List, Set, fromJS } from 'immutable';
import _ from 'lodash';

import { getOpenHeaderPaths } from '../lib/org_utils';

import { restoreBaseSettings } from '../actions/base';
import { restoreCaptureSettings } from '../actions/capture';
import { restoreFileSettings } from '../actions/org';

import generateId from '../lib/id_generator';
import { loadFilesFromLocalStorage } from './file_persister';

export const localStorageAvailable = (() => {
  try {
    localStorage.setItem('test', 'test');
    const localStorageRes = localStorage.getItem('test') === 'test';
    localStorage.removeItem('test');
    return localStorageRes && localStorage;
  } catch (e) {
    return false;
  }
})();

/**
 * GitLab doesn't allow updating a file that doesn't exist or creating one that already exists, so
 * need to figure out which to do.
 */
const updateConfigForGitLab = async (client, contents) => {
  const filename = '/.organice-config.json';
  let exists = false;
  try {
    const existingContents = await client.getFileContents(filename);
    exists = true;
    // INFO: Not calling syncBackendClient.createFile is a
    // workaround for
    // https://github.com/200ok-ch/organice/issues/736
    if (existingContents === contents) {
      return;
    }
  } catch {
    // Error is (presumably) because file doesn't exist. Unfortunately this isn't perfect because it
    // could have failed due to e.g. a network issue.
  }
  if (exists) {
    await client.updateFile(filename, contents);
  } else {
    await client.createFile(filename, contents);
  }
};

const debouncedPushConfigToSyncBackend = _.debounce(
  (syncBackendClient, contents) => {
    switch (syncBackendClient.type) {
      case 'Dropbox':
      case 'WebDAV':
        syncBackendClient
          .createFile('/.organice-config.json', contents)
          .catch((error) =>
            alert(`There was an error trying to push settings to your sync backend: ${error}`)
          );
        break;
      case 'GitLab':
        updateConfigForGitLab(syncBackendClient, contents).catch((error) =>
          alert(`There was an error trying to push settings to your sync backend: ${error}`)
        );
        break;
      default:
    }
  },
  1000,
  { maxWait: 3000 }
);

export const persistableFields = [
  {
    category: 'base',
    name: 'fontSize',
    type: 'nullable',
  },
  {
    category: 'base',
    name: 'bulletStyle',
    type: 'nullable',
    default: 'Fancy',
  },
  {
    category: 'base',
    name: 'shouldTapTodoToAdvance',
    type: 'boolean',
  },

  {
    category: 'base',
    name: 'agendaDefaultDeadlineDelayUnit',
    type: 'nullable',
  },
  {
    category: 'base',
    name: 'agendaDefaultDeadlineDelayValue',
    type: 'nullable',
  },
  {
    category: 'base',
    name: 'editorDescriptionHeightValue',
    type: 'nullable',
    default: '8',
  },
  {
    category: 'base',
    name: 'agendaStartOnWeekday',
    type: 'nullable',
    default: 1,
  },

  {
    category: 'base',
    name: 'shouldStoreSettingsInSyncBackend',
    type: 'boolean',
    default: true,
  },
  {
    category: 'base',
    name: 'lastSeenChangelogHash',
    type: 'nullable',
  },
  {
    category: 'base',
    name: 'customKeybindings',
    type: 'json',
  },
  {
    category: 'base',
    name: 'shouldLiveSync',
    type: 'boolean',
    default: true,
  },
  {
    category: 'base',
    name: 'showDeadlineDisplay',
    type: 'boolean',
    default: false,
  },
  {
    category: 'base',
    name: 'shouldSyncOnBecomingVisibile',
    type: 'boolean',
    default: true,
  },
  {
    category: 'base',
    name: 'shouldShowTitleInOrgFile',
    type: 'boolean',
  },
  {
    category: 'base',
    name: 'shouldLogIntoDrawer',
    type: 'boolean',
  },
  {
    // The focus dashboard's session log and reward list. Bounded in lib/focus.js so it
    // cannot grow without limit inside the config file.
    category: 'base',
    name: 'focusLog',
    type: 'json',
  },
  {
    // The week dashboard's completion target. Self-chosen, so it travels with the
    // rest of the settings rather than living only in one browser.
    category: 'base',
    name: 'weeklyTaskTarget',
    type: 'number',
  },
  {
    // The dashboard's own buttons. A persisted field rather than the dashboard's
    // private localStorage entry, so it travels to /.organice-config.json in the
    // sync backend like every other setting and comes back on another device.
    //
    // INFO: The name deliberately matches the localStorage key the dashboard used
    // before, so a configuration built in the browser is picked up as-is instead of
    // being reset the first time this runs.
    category: 'base',
    name: 'dashboardConfig',
    type: 'json',
  },
  {
    category: 'base',
    name: 'snippetsRepositoryUrl',
    type: 'string',
    default: '',
  },
  {
    category: 'base',
    name: 'fastDaystartFolder',
    type: 'string',
    default: '',
  },
  {
    category: 'base',
    name: 'slowDaystartFolder',
    type: 'string',
    default: '',
  },
  {
    // The working window pace is measured against — see doc/contract-motivation-concept.org
    // §4.3. Hours 0–24; lib/contract.js falls back to 8–18 on anything degenerate.
    // The contract itself is never a persisted field: it lives only as properties
    // of the '* Tagesvertrag' node inside each day's daily note.
    category: 'base',
    name: 'contractDayStartHour',
    type: 'number',
  },
  {
    category: 'base',
    name: 'contractDayEndHour',
    type: 'number',
  },
  {
    // The one file the team dashboard reads and writes — see
    // doc/team-dashboard-concept.org §5. Unlike the contract, none of that record
    // is daily-cadence data, so it lives in a single persistent file (the way
    // gtdPath does) rather than in the daily-note window. Empty falls back to
    // DEFAULT_TEAM_ADOPTION_PATH in lib/team.js.
    category: 'base',
    name: 'teamAdoptionPath',
    type: 'string',
    default: '',
  },
  {
    // How often the page reminds the lead to run an actual pulse with the team
    // (concept §4.5). lib/team.js falls back to 21 days on anything degenerate.
    category: 'base',
    name: 'teamPulseIntervalDays',
    type: 'number',
  },
  {
    // The one file the pastoral formation dashboard reads and writes — see
    // doc/pastoral-formation-concept.org §5.2. One persistent file for the same
    // reason as teamAdoptionPath: an encounter or a formation conversation lands
    // on whatever day it lands on. Empty falls back to DEFAULT_FORMATION_PATH in
    // lib/formation.js.
    category: 'base',
    name: 'formationPath',
    type: 'string',
    default: '',
  },
  {
    // How long before a dimension counts as cold and the formation conversation
    // is due (concept §4.1, §4.4). One number for both, because per-dimension
    // intervals would be four more settings for a benefit nobody has measured
    // (§7.2). lib/formation.js falls back to 28 days on anything degenerate.
    category: 'base',
    name: 'formationIntervalDays',
    type: 'number',
  },
  {
    // The one file the daily review dashboard reads and writes — see
    // doc/daily-review-concept.org §5.2. Empty falls back to
    // DEFAULT_REVIEW_PATH in lib/review.js.
    category: 'base',
    name: 'reviewPath',
    type: 'string',
    default: '',
  },
  {
    // Which evening the weekly thankfulness prompt appears on, 0 = Sunday.
    // Weekly rather than daily is an evidence claim rather than a preference
    // (concept §2.1): counting blessings once a week raised well-being where
    // three times a week did not.
    category: 'base',
    name: 'gratitudeWeekday',
    type: 'number',
  },
  {
    // Whether the evening review shows today's Tagesvertrag read-only above its
    // questions (concept §4.1). The one cross-dashboard read in the whole set.
    category: 'base',
    name: 'reviewContractIntegration',
    type: 'boolean',
    default: true,
  },
  {
    // org-roam's `org-roam-directory`: everything at or below this path is
    // scanned for nodes. Empty falls back to DEFAULT_ROAM_DIRECTORY in
    // lib/org_roam.js. Narrower is faster — the scan is one request per
    // directory plus one per file.
    category: 'base',
    name: 'roamDirectory',
    type: 'string',
    default: '',
  },
  {
    category: 'base',
    name: 'closeSubheadersRecursively',
    type: 'boolean',
  },
  {
    category: 'base',
    name: 'shouldNotIndentOnExport',
    type: 'boolean',
  },
  {
    category: 'org',
    name: 'showClockDisplay',
    type: 'boolean',
  },
  {
    category: 'base',
    name: 'colorScheme',
    type: 'string',
    default: 'OS',
  },
  {
    category: 'base',
    name: 'theme',
    type: 'string',
    default: 'Solarized',
  },
  {
    category: 'capture',
    name: 'captureTemplates',
    type: 'json',
    default: List(),
  },
  {
    category: 'org',
    name: 'fileSettings',
    type: 'json',
    default: List(),
  },
  {
    category: 'base',
    name: 'agendaTimeframe',
    type: 'string',
    default: 'Week',
  },
  {
    category: 'base',
    name: 'preferEditRawValues',
    type: 'boolean',
  },
  {
    category: 'base',
    name: 'orgHabitShowAllToday',
    type: 'boolean',
    default: false,
  },
  {
    category: 'base',
    name: 'orgHabitPrecedingDays',
    type: 'number',
    default: 21,
  },
  {
    category: 'base',
    name: 'orgHabitFollowingDays',
    type: 'number',
    default: 7,
  },
  {
    category: 'org',
    name: 'bookmarks',
    type: 'json',
    default: Map({
      search: List(),
      'task-list': List(),
      refile: List(),
    }),
  },
];

export const readOpennessState = () => {
  const opennessStateJSONString = localStorage.getItem('headerOpenness');
  return !!opennessStateJSONString ? JSON.parse(opennessStateJSONString) : null;
};

const getFieldsToPersist = (state, fields) => {
  return fields
    .filter((field) => !field.depreacted)
    .filter((field) => field.category === 'org')
    .map((field) =>
      field.type === 'json'
        ? [field.name, JSON.stringify(state.org.present.get(field.name) || field.default || {})]
        : [field.name, state.org.present.get(field.name)]
    )
    .concat(
      persistableFields
        .filter((field) => field.category !== 'org')
        .map((field) => {
          return field.type === 'json'
            ? [
                field.name,
                JSON.stringify(state[field.category].get(field.name) || field.default || {}),
              ]
            : [field.name, state[field.category].get(field.name) || field.default];
        })
    );
};

const getConfigFileContents = (fieldsToPersist) => {
  return JSON.stringify(_.fromPairs(fieldsToPersist), null, 2);
};

export const applyCategorySettingsFromConfig = (state, config, category) => {
  persistableFields
    .filter((field) => field.category === category)
    .forEach((field) => {
      // Only set value if it exists in config to avoid overwriting localStorage values with undefined
      if (config[field.name] !== undefined) {
        field.type === 'json'
          ? (state = state.set(field.name, fromJS(JSON.parse(config[field.name]))))
          : (state = state.set(field.name, config[field.name]));
      }
    });

  return state;
};

export const applyCaptureSettingsFromConfig = (state, config) => {
  const captureTemplates = fromJS(JSON.parse(config.captureTemplates)).map((template) =>
    template.set('id', generateId())
  );

  return state.set('captureTemplates', captureTemplates);
};
export const applyFileSettingsFromConfig = (state, config) => {
  const fileSettings = fromJS(JSON.parse(config.fileSettings)).map((setting) =>
    setting.set('id', generateId())
  );

  return state.set('fileSettings', fileSettings);
};

const getInitialStateWithDefaultValues = () => {
  let initialState = {
    syncBackend: Map(),
    org: {
      past: [],
      present: Map({
        files: Map(),
        fileSettings: [],
        opennessState: Map(),
        search: Map({
          searchFilter: '',
          searchFilterExpr: [],
        }),
        bookmarks: Map({
          search: List(),
          'task-list': List(),
          refile: List(),
        }),
      }),
      future: [],
    },
    base: Map({ isLoading: Set(), finderTab: 'Search' }),
    capture: Map(),
  };

  persistableFields.forEach((field) => {
    const value = field.default;

    if (field.category === 'org') {
      initialState[field.category].present = initialState[field.category].present.set(
        field.name,
        value
      );
    } else {
      initialState[field.category] = initialState[field.category].set(field.name, value);
    }
  });

  return initialState;
};

const loadContentFromLocalStorage = (initialState) => {
  persistableFields.forEach((field) => {
    let value = localStorage.getItem(field.name);

    if (field.type === 'nullable') {
      if (value === 'null') {
        value = null;
      }
    } else if (field.type === 'boolean') {
      value = value === 'true';
    } else if (field.type === 'number') {
      if (value) {
        value = parseInt(value, 10);
      }
    } else if (field.type === 'json') {
      if (!value) {
        value = field.default || Map();
      } else {
        value = fromJS(JSON.parse(value));
      }
    }
    // When nothing has been saved to localStorage before, keep the default.
    value = value || field.default;

    if (field.category === 'org') {
      initialState[field.category].present = initialState[field.category].present.set(
        field.name,
        value
      );
    } else {
      initialState[field.category] = initialState[field.category].set(field.name, value);
    }
  });

  // Assign new ids to the capture templates.
  if (initialState.capture.get('captureTemplates')) {
    initialState.capture = initialState.capture.update('captureTemplates', (templates) =>
      templates.map((template) => template.set('id', generateId()))
    );
  }
  // Assign new ids to the file settings.
  if (initialState.org.present.get('fileSettings')) {
    initialState.org.present = initialState.org.present.update('fileSettings', (settings) =>
      settings.map((setting) => setting.set('id', generateId()))
    );
  }

  const opennessState = readOpennessState();
  if (!!opennessState) {
    initialState.org.present = initialState.org.present.set('opennessState', fromJS(opennessState));
  }

  // Cache the config file contents locally so we don't overwrite on
  // initial page load.
  window.previousSettingsFileContents = getConfigFileContents(
    getFieldsToPersist(initialState, persistableFields)
  );

  return loadFilesFromLocalStorage(initialState);
};

// Which repository (sync backend + project/endpoint) the files cache and
// settings in localStorage belong to, or null when signed out.
export const currentRepositoryIdentity = () => {
  switch (getPersistedField('authenticatedSyncService', true)) {
    case 'GitLab':
      return `GitLab:${getPersistedField('gitLabProject', true)}`;
    case 'WebDAV':
      return `WebDAV:${getPersistedField('webdavEndpoint', true)}`;
    case 'Dropbox':
      return 'Dropbox';
    default:
      return null;
  }
};

const REPOSITORY_CACHE_OWNER = 'repositoryCacheOwner';

// Drops everything in localStorage that was loaded from, or would be written
// back to, the repository: cached file contents and their dirty flags, header
// openness, and the settings mirrored in /.organice-config.json. Sign-in
// state (authenticatedSyncService, tokens, gitLabProject) is kept.
export const clearRepositoryCache = () => {
  if (!localStorageAvailable) return;
  const repositoryBoundKeys = [
    'persistedFiles',
    'isDirty',
    'headerOpenness',
    REPOSITORY_CACHE_OWNER,
    ...persistableFields.map((field) => field.name),
  ];
  Object.keys(localStorage)
    .filter((key) => key.startsWith('files__') || repositoryBoundKeys.includes(key))
    .forEach((key) => localStorage.removeItem(key));
};

// The files cache is keyed by path alone, so after switching repositories
// the old repository's /roam/gtd.org would load -- still flagged dirty if it
// had unsynced edits -- and be synced over the new repository's file; its
// settings would likewise be pushed into the new /.organice-config.json.
// Flush the cache whenever the repository it was filled from is not the one
// signed in now. A cache without an owner (written before this check
// existed) is adopted by the current repository.
let loadedRepositoryIdentity = null;

// False once another tab has signed in to a different repository: this tab
// still holds the old repository's files in memory and must no longer write
// them to the localStorage the other tab now reads.
export const isLoadedRepositoryCurrent = () =>
  !localStorageAvailable ||
  loadedRepositoryIdentity === null ||
  currentRepositoryIdentity() === loadedRepositoryIdentity;

export const guardRepositoryCache = () => {
  if (!localStorageAvailable) return;
  const identity = currentRepositoryIdentity();
  loadedRepositoryIdentity = identity;
  if (!identity) return;
  const owner = localStorage.getItem(REPOSITORY_CACHE_OWNER);
  if (owner && owner !== identity) {
    console.warn(`Repository changed from ${owner} to ${identity}; flushing the local cache.`);
    clearRepositoryCache();
  }
  localStorage.setItem(REPOSITORY_CACHE_OWNER, identity);
};

export const readInitialState = () => {
  let initialState = getInitialStateWithDefaultValues();

  guardRepositoryCache();

  return localStorageAvailable ? loadContentFromLocalStorage(initialState) : initialState;
};

export const loadSettingsFromConfigFile = (dispatch, getState) => {
  const syncBackendClient = getState().syncBackend.get('client');
  if (!syncBackendClient) {
    return;
  }

  let fileContentsPromise = null;
  switch (syncBackendClient.type) {
    case 'Dropbox':
    case 'GitLab':
    case 'WebDAV':
      fileContentsPromise = syncBackendClient.getFileContents('/.organice-config.json');
      break;
    default:
  }

  fileContentsPromise
    .then((configFileContents) => {
      try {
        let config;

        /* Rationale for the type check:
          When loading the settings file, `configFileContents` is
          sometimes already an object. Therefore, when JSON.parse is
          run, it throws an error which is silently swallowed by the
          catch below. This appears to be the cause of
          https:github.com/200ok-ch/organice/issues/472. Settings will
          never load from file and default config always loads.
          */
        if (typeof configFileContents === 'string') {
          config = JSON.parse(configFileContents);
        } else {
          config = configFileContents;
        }

        dispatch(restoreBaseSettings(config));
        dispatch(restoreCaptureSettings(config));
        dispatch(restoreFileSettings(config));
      } catch (_error) {
        // Something went wrong parsing the config file, but we don't care, we'll just
        // overwrite it with a good local copy.
      }
    })
    .catch(() => {});
};

export const subscribeToChanges = (store) => {
  if (!localStorageAvailable) {
    return () => {};
  } else {
    return () => {
      if (!isLoadedRepositoryCurrent()) return;
      const state = store.getState();

      const fieldsToPersist = getFieldsToPersist(state, persistableFields);

      fieldsToPersist.forEach(([name, value]) => {
        if (name && value) localStorage.setItem(name, value);
      });

      if (state.base.get('shouldStoreSettingsInSyncBackend')) {
        const settingsFileContents = getConfigFileContents(fieldsToPersist);

        if (window.previousSettingsFileContents !== settingsFileContents) {
          debouncedPushConfigToSyncBackend(state.syncBackend.get('client'), settingsFileContents);
        }

        window.previousSettingsFileContents = settingsFileContents;
      }

      const currentFilePath = state.org.present.get('path');
      const headers = state.org.present.getIn(['files', currentFilePath, 'headers']);
      if (!!currentFilePath && headers) {
        const openHeaderPaths = getOpenHeaderPaths(headers);

        let opennessState = {};
        const opennessStateJSONString = localStorage.getItem('headerOpenness');
        if (opennessStateJSONString) {
          opennessState = JSON.parse(opennessStateJSONString);
        }

        opennessState[currentFilePath] = openHeaderPaths;
        localStorage.setItem('headerOpenness', JSON.stringify(opennessState));
      }
    };
  }
};

export const persistField = (field, value) => {
  if (!localStorageAvailable) {
    return;
  } else {
    localStorage.setItem(field, value);
  }
};

export const getPersistedField = (field, nullable = false) => {
  if (!localStorageAvailable) {
    return null;
  } else {
    const value = localStorage.getItem(field);
    if (nullable) {
      return value === 'null' ? null : value;
    } else {
      return value;
    }
  }
};
